import React, { useMemo, useEffect, useRef } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { Category } from "../types/form";
import { filterVisibleCategories } from "../utils/categoryVisibility";

interface FormSidebarProps {
  categories: Category[];
  activeCategoryId: number | null; // The ID of the currently selected "content" category
  expandedCategories: Set<number>; // IDs of expanded parents
  onToggleExpand: (categoryId: number) => void;
  onSelectCategory: (category: Category) => void;
  title: string;
  formData: Record<string, any>; // Form data for visibility evaluation
  /** Form-wide visible question ids, transitive (client worklist #84). */
  visibleIds?: Set<number>;
}

const SidebarItem: React.FC<{
  category: Category;
  depth: number;
  activeCategoryId: number | null;
  expandedCategories: Set<number>;
  onToggleExpand: (id: number) => void;
  onSelectCategory: (cat: Category) => void;
  parentIsActive?: boolean;
}> = ({
  category,
  depth,
  activeCategoryId,
  expandedCategories,
  onToggleExpand,
  onSelectCategory,
  parentIsActive = false,
}) => {
  const hasSubcategories =
    category.subcategories && category.subcategories.length > 0;
  const isExpanded = expandedCategories.has(category.id);
  const isActive = activeCategoryId === category.id;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasSubcategories) {
      onToggleExpand(category.id);
    } else {
      onSelectCategory(category);
    }
  };

  // Check if this category is active OR if any of its subcategories is active
  const hasActiveSubcategory =
    hasSubcategories &&
    category.subcategories!.some(
      (sub) =>
        sub.id === activeCategoryId ||
        (sub.subcategories &&
          sub.subcategories.some((subSub) => subSub.id === activeCategoryId))
    );
  const isContainerActive = depth === 0 && (isActive || hasActiveSubcategory);
  const containerHasBackground =
    isContainerActive || (depth > 0 && parentIsActive);

  return (
    <div
      className={`mb-1 ${depth === 0 ? "mb-3" : ""} ${
        isContainerActive ? "bg-[#94EFDE] rounded-lg py-2" : ""
      }`}
    >
      {/* Category Header */}
      <div
        onClick={handleClick}
        data-category-id={category.id}
        className={`
          flex justify-between items-center p-3 cursor-pointer 
          ${depth === 0 ? "rounded-tl-lg rounded-tr-lg" : "rounded-md"} 
          text-base text-[#1B365D] transition-colors
          ${isActive ? "font-bold" : "font-[400]"}
          ${depth > 0 ? "pl-" + (depth * 4 + 3) : ""} 
          ${depth > 0 && isActive ? "bg-white" : "bg-transparent"}
          ${depth === 0 && !isActive ? "hover:bg-gray-50" : ""}
        `}
        style={{ paddingLeft: depth > 0 ? `${depth * 16 + 12}px` : undefined }}
      >
        <span>{category.name}</span>
        {hasSubcategories && (
          <span>
            {isExpanded ? (
              <IoIosArrowUp className="text-xl text-[#1B365D]" />
            ) : (
              <IoIosArrowDown className="text-xl text-[#1B365D]" />
            )}
          </span>
        )}
      </div>

      {/* Render Subcategories */}
      {hasSubcategories && isExpanded && (
        <div
          className={`
            ${depth === 0 ? "rounded-bl-lg rounded-br-lg px-4" : ""}
          `}
        >
          {category.subcategories!.map((sub) => (
            <SidebarItem
              key={sub.id}
              category={sub}
              depth={depth + 1}
              activeCategoryId={activeCategoryId}
              expandedCategories={expandedCategories}
              onToggleExpand={onToggleExpand}
              onSelectCategory={onSelectCategory}
              parentIsActive={isActive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FormSidebar: React.FC<FormSidebarProps> = ({
  categories,
  activeCategoryId,
  expandedCategories,
  onToggleExpand,
  onSelectCategory,
  title,
  formData,
  visibleIds,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Filter categories to only show those with visible questions
  const visibleCategories = useMemo(() => {
    return filterVisibleCategories(categories, formData, visibleIds);
  }, [categories, formData, visibleIds]);

  // Scroll to active category when it changes
  useEffect(() => {
    if (!activeCategoryId || !sidebarRef.current) return;

    // Small delay to ensure DOM is updated (especially after expanding categories)
    const timeoutId = setTimeout(() => {
      const activeElement = sidebarRef.current?.querySelector(
        `[data-category-id="${activeCategoryId}"]`
      ) as HTMLElement;

      if (activeElement && sidebarRef.current) {
        const sidebarContainer = sidebarRef.current;
        const elementTop = activeElement.offsetTop;
        const elementHeight = activeElement.offsetHeight;
        const containerHeight = sidebarContainer.clientHeight;

        // Calculate the position to center the element in the viewport
        const scrollToPosition =
          elementTop + elementHeight / 2 - containerHeight / 2;

        sidebarContainer.scrollTo({
          top: Math.max(0, scrollToPosition),
          behavior: "smooth",
        });
      }
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [activeCategoryId]);

  return (
    <div
      ref={sidebarRef}
      className="hidden md:block w-[25%] shrink-0 bg-white shadow-[0px_1px_2px_0px_#0000000D] p-6 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-500 scrollbar-track-transparent md:max-h-[80vh] rounded-lg pb-10 sticky top-[40px] self-start"
    >
      <h2 className="pera font-georgia leading-8 mb-6">{title}</h2>
      {visibleCategories.map((category) => (
        <SidebarItem
          key={category.id}
          category={category}
          depth={0}
          activeCategoryId={activeCategoryId}
          expandedCategories={expandedCategories}
          onToggleExpand={onToggleExpand}
          onSelectCategory={onSelectCategory}
        />
      ))}
    </div>
  );
};

export default FormSidebar;
